<template>
  <div class="shop-booking">
    <h1>การชำระเงินที่รอการอนุมัติ</h1>
    
    <!-- Payment Requests Section -->
    <div class="section">
      <h2>คำขอชำระเงิน (Payment Requests)</h2>
      <div v-if="loading" class="loading">
        <div class="spinner"></div>
        <p>กำลังโหลดข้อมูล...</p>
      </div>
      <div v-else-if="paymentRequests.length === 0" class="no-data">
        ไม่มีคำขอชำระเงินที่รอการอนุมัติ
      </div>
      <div v-else class="payment-list">
        <div v-for="request in paymentRequests" :key="request.bookingCode" class="payment-card">
          <div class="payment-header">
            <h3>รหัสการจอง: {{ request.bookingCode }}</h3>
            <span class="status pending">รอการอนุมัติ</span>
          </div>
          <div class="payment-details">
            <p><strong>จำนวนเงิน:</strong> {{ request.amount }} บาท</p>
            <p><strong>วันที่สร้าง:</strong> {{ formatDate(request.createdAt) }}</p>
          </div>
          <div class="payment-actions">
            <button @click="viewPaymentSlip(request)" class="btn view-btn">
              ดูสลิป
            </button>
            <button @click="approvePayment(request)" class="btn approve-btn">
              อนุมัติ
            </button>
            <button @click="rejectPayment(request)" class="btn reject-btn">
              ปฏิเสธ
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Payment Slip Modal -->
    <div v-if="showSlipModal" class="modal">
      <div class="modal-content">
        <span class="close" @click="showSlipModal = false">&times;</span>
        <h2>สลิปการชำระเงิน</h2>
        <img :src="selectedSlipUrl" alt="Payment Slip" class="slip-image">
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue';
import { collection, query, where, getDocs, updateDoc, doc } from 'firebase/firestore';
import { db } from '../firebase/firebaseConfig';
import { auth } from '../firebase/firebaseConfig';

export default {
  name: 'ShopBookingApprove',
  setup() {
    const loading = ref(true);
    const paymentRequests = ref([]);
    const showSlipModal = ref(false);
    const selectedSlipUrl = ref('');

    // Fetch payment requests
    const fetchPaymentRequests = async () => {
      try {
        const q = query(collection(db, 'payment_requests'), where('status', '==', 'pending'));
        const querySnapshot = await getDocs(q);
        paymentRequests.value = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (error) {
        console.error('Error fetching payment requests:', error);
      }
    };

    // Format date
    const formatDate = (timestamp) => {
      if (!timestamp) return '';
      const date = timestamp.toDate();
      return date.toLocaleString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    };

    // View payment slip
    const viewPaymentSlip = (payment) => {
      selectedSlipUrl.value = payment.paymentReceipt;
      showSlipModal.value = true;
    };

    // Approve payment
    const approvePayment = async (payment) => {
      if (!confirm('คุณต้องการอนุมัติการชำระเงินนี้ใช่หรือไม่?')) return;

      try {
        // อัพเดทสถานะใน payment_requests
        const paymentRef = doc(db, 'payment_requests', payment.id);
        await updateDoc(paymentRef, {
          status: 'completed',
          approvedAt: new Date(),
          approvedBy: auth.currentUser.email
        });

        // อัพเดทสถานะใน bookings
        const bookingRef = doc(db, 'bookings', payment.bookingCode);
        await updateDoc(bookingRef, {
          depositPaid: true,
          depositPaidAt: new Date()
        });

        // รีเฟรชข้อมูล
        await fetchPaymentRequests();
      } catch (error) {
        console.error('Error approving payment:', error);
        alert('เกิดข้อผิดพลาดในการอนุมัติการชำระเงิน');
      }
    };

    // Reject payment
    const rejectPayment = async (payment) => {
      if (!confirm('คุณต้องการปฏิเสธการชำระเงินนี้ใช่หรือไม่?')) return;

      try {
        // อัพเดทสถานะใน payment_requests
        const paymentRef = doc(db, 'payment_requests', payment.id);
        await updateDoc(paymentRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser.email
        });

        // อัพเดทสถานะใน bookings
        const bookingRef = doc(db, 'bookings', payment.bookingCode);
        await updateDoc(bookingRef, {
          status: 'rejected',
          rejectedAt: new Date(),
          rejectedBy: auth.currentUser.email
        });

        // รีเฟรชข้อมูล
        await fetchPaymentRequests();
      } catch (error) {
        console.error('Error rejecting payment:', error);
        alert('เกิดข้อผิดพลาดในการปฏิเสธการชำระเงิน');
      }
    };

    // Initial data fetch
    onMounted(async () => {
      try {
        await fetchPaymentRequests();
      } finally {
        loading.value = false;
      }
    });

    return {
      loading,
      paymentRequests,
      showSlipModal,
      selectedSlipUrl,
      formatDate,
      viewPaymentSlip,
      approvePayment,
      rejectPayment
    };
  }
};
</script>

<style scoped>
.shop-booking {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.section {
  margin-bottom: 40px;
}

h1 {
  color: #333;
  margin-bottom: 30px;
}

h2 {
  color: #444;
  margin-bottom: 20px;
}

.loading {
  text-align: center;
  padding: 20px;
}

.spinner {
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin: 0 auto 10px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-data {
  text-align: center;
  padding: 20px;
  color: #666;
  background: #f5f5f5;
  border-radius: 8px;
}

.payment-list {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
}

.payment-card {
  background: white;
  border-radius: 8px;
  padding: 20px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 15px;
}

.payment-header h3 {
  margin: 0;
  color: #333;
}

.status {
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 0.9em;
}

.status.pending {
  background: #fff3cd;
  color: #856404;
}

.payment-details {
  margin-bottom: 15px;
}

.payment-details p {
  margin: 5px 0;
  color: #666;
}

.payment-actions {
  display: flex;
  gap: 10px;
}

.btn {
  padding: 8px 16px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: background-color 0.2s;
}

.view-btn {
  background: #e9ecef;
  color: #495057;
}

.approve-btn {
  background: #28a745;
  color: white;
}

.reject-btn {
  background: #dc3545;
  color: white;
}

.btn:hover {
  opacity: 0.9;
}

.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0,0,0,0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background: white;
  padding: 20px;
  border-radius: 8px;
  max-width: 90%;
  max-height: 90vh;
  position: relative;
}

.close {
  position: absolute;
  right: 20px;
  top: 10px;
  font-size: 24px;
  cursor: pointer;
  color: #666;
}

.slip-image {
  max-width: 100%;
  max-height: 70vh;
  object-fit: contain;
}
</style> 